import { useQuery } from "@tanstack/react-query";
import { salesDb } from "@/integrations/supabase/sales-client";
import { supabase } from "@/integrations/supabase/client";
import type { StudentKind } from "./useEventBalances";

export type AssignmentRow = {
  id: string;
  event_id: string;
  fishsix_student_code: string | null;
  esc_student_code: string | null;
  knoware_student_code: string | null;
  cashback_id: string | null;
  refund_cashback_id: string | null;
  assigned_by: string | null;
  assigned_at: string;
  unassigned_at: string | null;
  unassigned_by: string | null;
  status: string;
  student_code?: string | null;
  student_kind?: StudentKind | null;
  student_first_name?: string | null;
  student_last_name?: string | null;
  student_nickname?: string | null;
  assigned_by_name?: string | null;
};

export const useEventAssignments = (eventId: string | null) => {
  return useQuery({
    enabled: !!eventId,
    queryKey: ["event-assignments", eventId],
    queryFn: async () => {
      const { data, error } = await salesDb()
        .from("event_assignments")
        .select("*")
        .eq("event_id", eventId)
        .eq("status", "active")
        .order("assigned_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as AssignmentRow[];

      const fsCodes = Array.from(
        new Set(rows.map((r) => r.fishsix_student_code).filter(Boolean) as string[])
      );
      const escCodes = Array.from(
        new Set(rows.map((r) => r.esc_student_code).filter(Boolean) as string[])
      );
      const knCodes = Array.from(
        new Set(rows.map((r) => r.knoware_student_code).filter(Boolean) as string[])
      );
      const userIds = Array.from(
        new Set(rows.map((r) => r.assigned_by).filter(Boolean) as string[])
      );

      const [studentsRes, escRes, knRes, profilesRes] = await Promise.all([
        fsCodes.length
          ? (supabase as any).schema('student').from("fishsix_students").select("code,first_name,last_name,nickname").in("code", fsCodes)
          : Promise.resolve({ data: [], error: null } as any),
        escCodes.length
          ? (supabase as any).schema("esc").from("students").select("code,name,nickname").in("code", escCodes)
          : Promise.resolve({ data: [], error: null } as any),
        knCodes.length
          ? salesDb()
              .from("knoware_students_view")
              .select("code,student_name_th,student_nickname_th")
              .in("code", knCodes)
          : Promise.resolve({ data: [], error: null } as any),
        userIds.length
          ? supabase.from("profiles").select("id,full_name,email").in("id", userIds)
          : Promise.resolve({ data: [], error: null } as any),
      ]);

      const fsMap = new Map<string, any>();
      (studentsRes.data ?? []).forEach((s: any) => fsMap.set(s.code, s));
      const escMap = new Map<string, any>();
      (escRes.data ?? []).forEach((s: any) => escMap.set(s.code, s));
      const knMap = new Map<string, any>();
      (knRes.data ?? []).forEach((s: any) => knMap.set(s.code, s));
      const pMap = new Map<string, any>();
      (profilesRes.data ?? []).forEach((p: any) => pMap.set(p.id, p));

      return rows.map((r) => {
        const p = r.assigned_by ? pMap.get(r.assigned_by) : null;
        const assigned_by_name = p?.full_name ?? p?.email ?? null;
        if (r.fishsix_student_code) {
          const s = fsMap.get(r.fishsix_student_code);
          return {
            ...r,
            student_code: r.fishsix_student_code,
            student_kind: "fs",
            student_first_name: s?.first_name ?? null,
            student_last_name: s?.last_name ?? null,
            student_nickname: s?.nickname ?? null,
            assigned_by_name,
          } as AssignmentRow;
        }
        if (r.esc_student_code) {
          const s = escMap.get(r.esc_student_code);
          return {
            ...r,
            student_code: r.esc_student_code,
            student_kind: "esc",
            student_first_name: s?.name ?? null,
            student_last_name: null,
            student_nickname: s?.nickname ?? null,
            assigned_by_name,
          } as AssignmentRow;
        }
        if (r.knoware_student_code) {
          const s = knMap.get(r.knoware_student_code);
          return {
            ...r,
            student_code: r.knoware_student_code,
            student_kind: "kn",
            student_first_name: s?.student_name_th ?? null,
            student_last_name: null,
            student_nickname: s?.student_nickname_th ?? null,
            assigned_by_name,
          } as AssignmentRow;
        }
        return {
          ...r,
          student_code: null,
          student_kind: null,
          assigned_by_name,
        } as AssignmentRow;
      });
    },
  });
};
